import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import DocumentTitle from 'react-document-title';
import {
	Alert,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Form,
	Input,
	Label,
	Row,
	Progress,
	UncontrolledTooltip
} from 'reactstrap';
import { Line, Chart } from 'react-chartjs-2';
import 'chartjs-plugin-annotation';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import ReactGA from 'react-ga';
import Select from 'react-select';

import {
	fetchNewestDate,
	resetNewestDate,
	fetchSmelterCapacity,
	resetSmelterCapacity,
	fetchPseudoGroups
} from 'actions';
import { cChartOptions, genChartStyle, plugins } from './chartConfig';
import { Crumbs } from 'containers';
import { DateRangePopover } from 'components';
import { multiSelectStyle } from './multiSelectStyle';
import { smeltersCapacityInlineHelpContent } from './smeltersCapacityInlineHelpContent';

import './Smelters.scss';

const config = {
	dateRangeDayLimit: 10,
	apiDateFormat: 'YYYYMMDD',
	dspDateFormat: 'YYYY-MM-DD',
	globalRegion: {
		name: 'Inactive Capacity Index',
		colour: '#FFA500',
		title: 'Global'
	},
	combinedRegion: {
		name: 'Combined index',
		colour: '#FFA500',
		title: 'Combine multiple regions'
	},
	fakePseudoGroups: [
		{
			name: 'Global (ex. CN)',
			key: 'globalexchina'
		},
		{
			name: 'China',
			key: 'china'
		},
		{
			name: 'Asia and Oceania',
			key: 'asiaoceania'
		},
		{
			name: 'Europe',
			key: 'europe'
		},
		{
			name: 'Africa',
			key: 'africa'
		},
		{
			name: 'North America',
			key: 'namerica'
		},
		{
			name: 'South America',
			key: 'samerica'
		}
	]
};

class SmeltersCapacity extends Component {
	state = {
		groupType: 'default',
		global: true,
		regions: [],
		countryRegions: [],
		chinaRegions: [],
		categoryRegions: [],
		selectedRegions: [],
		compareCombine: '',
		dateMin: moment.utc('2016-03-01'),
		dateMax: moment.utc(),
		dateFrom: moment.utc(),
		dateTo: moment.utc(),
		isInitDateRange: false,
		shortDateRange: false,
		showDataWarning: false
	};

	async componentDidMount() {
		plugins.forEach((plugin) => {
			//register plugin if it is not already registered
			if (!Chart.pluginService._plugins.includes(plugin)) {
				Chart.pluginService.register(plugin);
			}
		});

		this.props.fetchPseudoGroups({ ute: false });
		this.props.fetchNewestDate();
		ReactGA.pageview('/smelters/SmeltersCapacity', null, 'Smelter Capacity');
	}

	componentDidUpdate(
		{ newestDate: prevNewestDate },
		{
			isInitDateRange: prevIsInitDateRange,
			selectedRegions: prevSelectedRegions,
			global: prevGlobal,
			dateFrom: prevDateFrom,
			dateTo: prevDateTo
		}
	) {
		const { newestDate } = this.props;
		const { isInitDateRange, selectedRegions, global, dateFrom, dateTo } =
			this.state;

		//fetchNewestDate requested in ComponentDidMount(), ensure newestDate is received
		if (newestDate === '') {
			return;
		}

		//Date setting
		if (newestDate !== prevNewestDate) {
			this.updateDateHandling({ newestDate });
		}

		//once date range is set, fetch data
		if (isInitDateRange !== prevIsInitDateRange) {
			this.props.fetchSmelterCapacity({
				regions: 'global',
				dateFrom: dateFrom.format(config.apiDateFormat),
				dateTo: dateTo.format(config.apiDateFormat)
			});
		}

		if (
			((!global && global !== prevGlobal) || prevSelectedRegions.length > 0) &&
			selectedRegions.length === 0
		) {
			this.props.resetSmelterCapacity();
		}
		if (
			(global && global !== prevGlobal) ||
			(selectedRegions.length &&
				prevSelectedRegions.length !== selectedRegions.length) ||
			!prevSelectedRegions.every((ps) =>
				selectedRegions.some((s) => ps === s)
			) ||
			prevDateFrom !== dateFrom ||
			prevDateTo !== dateTo
		) {
			this.props.fetchSmelterCapacity({
				regions: global ? 'global' : selectedRegions.toString(),
				dateFrom: dateFrom.format(config.apiDateFormat),
				dateTo: dateTo.format(config.apiDateFormat)
			});
		}
	}

	componentWillUnmount() {
		this.props.resetSmelterCapacity();
		this.props.resetNewestDate();
	}

	async updateDateHandling({ newestDate }) {
		const maxDate = moment.utc(newestDate);

		//for enterprise user, savant-premium-plus, savant-premium
		let dateState = {
			dateFrom: maxDate.clone().subtract(2, 'y').subtract(1, 'd'),
			dateTo: maxDate.clone().subtract(1, 'd'),
			dateMax: maxDate.clone().subtract(1, 'd')
		};

		this.setState({
			...dateState,
			isInitDateRange: true
		});
	}

	// Radio button click for combine/compare
	async onRadioBtnClick(rSelected) {
		this.setState({ ...rSelected });
	}

	// Global button click
	async onGlobalBtnClick() {
		const { global, groupType } = this.state;
		let newState = {};
		//reset regions when switching to global, but only for current view
		if (!global) {
			if (groupType === 'default') {
				newState = { regions: [] };
			} else if (groupType === 'country') {
				newState = { countryRegions: [] };
			} else if (groupType === 'china') {
				newState = { chinaRegions: [] };
			} else if (groupType === 'category') {
				newState = { categoryRegions: [] };
			}
			newState = { ...newState, selectedRegions: [] };
		}
		//toggle global
		newState = { ...newState, global: !global };
		this.setState(newState);
	}

	// region buttons
	async onRegionBtnClick({ prop, val }) {
		//existing regions
		let regions = this.state[prop];
		const oldRegionsCount = regions.length;

		if (regions.includes(val)) {
			regions = regions.filter((item) => item !== val);
		} else {
			// deselect 'globalexchina' button if any button excluding 'china' button is clicked
			if (val !== 'china' && regions.includes('globalexchina')) {
				regions = regions.filter((item) => item !== 'globalexchina');
				//deselect 'non china' buttons if Global (ex. CN) is clicked
			} else if (val === 'globalexchina') {
				regions = regions.filter((item) => item === 'china');
			}
			regions = [...regions, val];
		}
		const sortedRegions = this.sortRegions(regions);
		let newState = {
			[prop]: sortedRegions,
			selectedRegions: sortedRegions,
			global: false
		};

		if (sortedRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}
		this.setState(newState);
	}

	async onSelectRegions(newRegions) {
		const { groupType, countryRegions, chinaRegions, categoryRegions } =
			this.state;

		if (!newRegions) {
			newRegions = [];
		}

		let oldRegionsCount = 0;
		let newState = {};

		if (groupType === 'country') {
			oldRegionsCount = countryRegions.length;
			newState = { ...newState, countryRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'china') {
			oldRegionsCount = chinaRegions.length;
			newState = { ...newState, chinaRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'category') {
			oldRegionsCount = categoryRegions.length;
			newState = { ...newState, categoryRegions: this.sortRegions(newRegions) };
		}

		const sortedRegions = this.sortRegions(
			newRegions.map((region) => region.value)
		);

		newState = {
			...newState,
			global: false,
			selectedRegions: sortedRegions
		};

		if (newRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}
		this.setState(newState);
	}

	// Region toggling for the select all / none button
	async onToggleAllRegions() {
		const { regions, compareCombine } = this.state;
		const { pseudoGroups } = this.props;

		//exclude 'globalexchina' from All
		const defaultGroups = pseudoGroups.filter(
			(g) => g.type === 'default' && g.key !== 'globalexchina'
		);

		const newRegions =
			regions.length < defaultGroups.length
				? defaultGroups.map((g) => g.key)
				: [];
		const newCompareCombine = regions.length > 1 ? compareCombine : 'combine';
		this.setState({
			regions: newRegions,
			selectedRegions: newRegions,
			compareCombine: newRegions.length ? newCompareCombine : '',
			global: false
		});
	}

	// Handle date range selection event
	async onDateRangeSelect({ startDate, endDate }) {
		//startDate, endDate - moment objects
		this.setState({
			dateFrom: startDate,
			dateTo: endDate,
			shortDateRange: endDate.diff(startDate, 'days') < config.dateRangeDayLimit
		});
	}

	//sort plain array of regions' names or array of region objects
	sortRegions = (regions) => {
		const { pseudoGroups } = this.props;
		const { groupType } = this.state;
		if (!regions.length) {
			return regions;
		}
		const regionOrder = pseudoGroups
			.filter((g) => g.type === groupType)
			.map((g) => g.key);

		return regionOrder.reduce((acc, r) => {
			let filtered = [];
			filtered = regions.filter((elem) => {
				return Object.keys(elem).length && Object.keys(elem).includes('value')
					? elem['value'] === r
					: elem === r;
			});

			if (filtered.length) {
				acc.push(filtered[0]);
			}
			return acc;
		}, []);
	};

	onInputSelect = async ({ target: { value, name } }) => {
		const { regions, countryRegions, chinaRegions, categoryRegions } =
			this.state;

		this.setState({
			[name]: value
		});
		if (name === 'groupType') {
			let selectedRegions = [];

			if (value === 'default') {
				selectedRegions = [...selectedRegions, ...regions];
			} else if (value === 'country') {
				selectedRegions = [
					...selectedRegions,
					...countryRegions.map((region) => region.value)
				];
			} else if (value === 'china') {
				selectedRegions = [
					...selectedRegions,
					...chinaRegions.map((region) => region.value)
				];
			} else if (value === 'category') {
				selectedRegions = [
					...selectedRegions,
					...categoryRegions.map((region) => region.value)
				];
			}
			const global = selectedRegions.length ? false : true;
			this.setState({
				selectedRegions: selectedRegions,
				global: global
			});
		}
	};

	getChartType() {
		const { dateFrom, dateTo } = this.state;
		const days = dateTo.diff(dateFrom, 'd');
		const type = 'f';
		let size = '';

		if (days <= 50) {
			size = 'sm';
		} else if (days > 50 && days <= 100) {
			size = 'md';
		} else if (days > 100 && days <= 200) {
			size = 'lg';
		} else if (days > 200) {
			size = 'xl';
		} else if (days > 300) {
			size = 'xxl';
		}
		return { size, type };
	}

	// Build the card header element for display
	buildCardHeader() {
		const { compareCombine, global } = this.state;

		let title = 'Capacity:';
		if (global) {
			title = `${title} ${config.globalRegion.title}`;
		} else if (compareCombine === 'combine') {
			title = `${title} ${config.combinedRegion.title}`;
		} else if (compareCombine === 'compare') {
			title = `${title} Compare multiple regions`;
		} else {
			title = `${title} One region`;
		}

		return (
			<CardHeader>
				<i className="fa fa-line-chart" aria-hidden="true" /> {title}
			</CardHeader>
		);
	}

	buildRegionButton({ id, name, title, tooltip = '', disabled = false }) {
		const { regions } = this.state;

		return (
			<Fragment key={`region${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() => this.onRegionBtnClick({ prop: 'regions', val: name })}
					active={regions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildCsvDownloadButton() {
		const {
			global,
			shortDateRange,
			regions,
			countryRegions,
			chinaRegions,
			categoryRegions,
			groupType
		} = this.state;
		const { smelterCapacity } = this.props;

		if (
			(global ||
				(groupType === 'default' && regions.length > 0) ||
				(groupType === 'country' && countryRegions.length > 0) ||
				(groupType === 'china' && chinaRegions.length > 0) ||
				(groupType === 'category' && categoryRegions.length > 0)) &&
			!shortDateRange &&
			smelterCapacity &&
			smelterCapacity.length !== 0
		) {
			let { csvHeaders, csvData, csvFileName } = this.getCsvDataFromProps();
			return (
				<CSVLink
					id="buttonDownload"
					filename={csvFileName}
					headers={csvHeaders}
					data={csvData}
					className="btn btn-success mb-3 pull-right btn-sm"
					onClick={() => {
						ReactGA.event({
							category: 'smelterCapacity',
							action: 'data downloaded',
							label: csvFileName
						});
						return true;
					}}
				>
					<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
					data
				</CSVLink>
			);
		}

		return (
			<Button
				id="buttonDownload"
				color="success"
				disabled
				className="mb-3 pull-right"
				size="sm"
			>
				<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
				data
			</Button>
		);
	}

	// Build the filter form
	buildFilterForm() {
		const {
			pseudoGroups = [],
			hasAdvancedGroups = false,
			countryGroups,
			chinaGroups,
			categoryGroups,
			newestDate
		} = this.props;
		const {
			dateMin,
			dateMax,
			dateFrom,
			dateTo,
			compareCombine,
			global,
			groupType,
			regions,
			countryRegions,
			chinaRegions,
			categoryRegions,
			selectedRegions
		} = this.state;

		const regionToggleHidden = pseudoGroups.length <= 2;
		const regionAllCopy =
			regions.length ===
			pseudoGroups.filter(({ type }) => type === 'default').length
				? 'None'
				: 'All';

		const orderedKeys = config.fakePseudoGroups.map(({ key }) => key);

		return (
			<Form>
				<Container fluid className="px-0 container-width">
					<Row noGutters>
						<Col xs="12" sm="12">
							{hasAdvancedGroups && (
								<Label for="groupType" className="mb-2 mr-3 align-top">
									<span className="sr-only">Geographical filtering type</span>
									<Input
										id="groupType"
										name="groupType"
										type="select"
										bsSize="sm"
										className="pointered"
										defaultValue={groupType}
										onChange={this.onInputSelect}
									>
										<option value="default">Region filtering</option>
										<option value="country">Country filtering</option>
										<option value="china">Chinese Region filtering</option>
										<option value="category">Smelter type filtering</option>
									</Input>
								</Label>
							)}

							<Button
								id="regionGlobal"
								outline
								size="sm"
								color="secondary"
								active={global}
								aria-label="global"
								className="mb-2 light-active-border"
								onClick={() => this.onGlobalBtnClick()}
							>
								Global
							</Button>

							{groupType === 'default' && pseudoGroups.length === 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{config.fakePseudoGroups.map(({ name, key }) => {
										return this.buildRegionButton({
											id: `region${key}`,
											name: key,
											title: name,
											disabled: true
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'default' && pseudoGroups.length > 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{pseudoGroups
										.filter(({ type }) => type === 'default')
										.sort(
											(a, b) =>
												orderedKeys.indexOf(a.key) - orderedKeys.indexOf(b.key)
										)
										.map(({ name, key, tooltip = '' }) => {
											return this.buildRegionButton({
												id: `region${key}`,
												name: key,
												title: name,
												tooltip
											});
										})}
								</ButtonGroup>
							)}

							{groupType === 'default' && (
								<Button
									id="regionAll"
									color="primary"
									size="sm"
									onClick={() => this.onToggleAllRegions()}
									className="mb-2 ml-1"
									style={{ width: '54px' }}
									disabled={global}
									hidden={regionToggleHidden}
								>
									{regionAllCopy}
								</Button>
							)}
							{groupType === 'country' && (
								<Select
									isMulti
									options={countryGroups}
									value={countryRegions}
									onChange={(val) => this.onSelectRegions(val)}
									className="react-select-container d-inline-block mb-2 ml-3"
									classNamePrefix="react-select"
									placeholder="Select countries..."
									styles={multiSelectStyle}
								/>
							)}
							{groupType === 'china' && (
								<Select
									isMulti
									options={chinaGroups}
									value={chinaRegions}
									onChange={(val) => this.onSelectRegions(val)}
									className="react-select-container d-inline-block mb-2 ml-3"
									classNamePrefix="react-select"
									placeholder="Select Chinese regions..."
									styles={multiSelectStyle}
								/>
							)}
							{groupType === 'category' && (
								<Select
									isMulti
									options={categoryGroups}
									value={categoryRegions}
									onChange={(val) => this.onSelectRegions(val)}
									className="react-select-container d-inline-block mb-2 ml-3"
									classNamePrefix="react-select"
									placeholder="Select smelters by type..."
									styles={multiSelectStyle}
								/>
							)}
						</Col>
					</Row>
					<Row noGutters>
						<Col xs="12" sm="12">
							<DateRangePopover
								id="dateSelector"
								startDate={dateFrom}
								endDate={dateTo}
								minDate={dateMin}
								maxDate={dateMax}
								newestDate={newestDate}
								onDateRangeSelected={this.onDateRangeSelect.bind(this)}
							/>
							<ButtonGroup className="mb-3 mr-3 align-bottom">
								<Button
									id="buttonCombine"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'combine' })
									}
									active={compareCombine === 'combine'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to combine smelter data"
								>
									Combine
								</Button>
								<Button
									id="buttonCompare"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'compare' })
									}
									active={compareCombine === 'compare'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to compare smelter data"
								>
									Compare
								</Button>
							</ButtonGroup>
							{this.buildCsvDownloadButton()}
						</Col>
					</Row>
				</Container>
			</Form>
		);
	}

	// Build csv data from the props for the download feature
	getCsvDataFromProps() {
		const {
			smelterCapacity: { compare = [], combine = [] },
			pseudoGroups
		} = this.props;
		let { dateFrom, dateTo, selectedRegions } = this.state;

		// Initialise our csv data
		let csvFileName = `cu-smelter-capacity-${dateFrom.format(
			config.apiDateFormat
		)}-${dateTo.format(config.apiDateFormat)}.csv`;
		let csvHeaders = [{ label: 'Date', key: 't' }];
		let csvData = [];

		const capacityData =
			selectedRegions.length === 0
				? [{ key: 'global', data: [...combine] }]
				: selectedRegions.length === 1
					? [{ key: selectedRegions[0], data: [...combine] }]
					: compare;

		capacityData.forEach(({ key, data }, i) => {
			const name =
				key !== 'global'
					? pseudoGroups.find((pg) => pg['key'] === key)['name']
					: 'Global';
			data.forEach(({ t, c }, j) => {
				// Add the time column value to the csv data if this is the first region iteration
				// or if we've somehow got a frame error from the api data and the row is missing
				if (i === 0 || !csvData[j]) {
					csvData[j] = {
						t: moment.utc(t).format(config.dspDateFormat)
					};
				}

				// Generate the region-based capacity column names
				let nameckey = `${name} Capacity (kt)`;

				// Add the column names to the csv headers if this is the first data iteration
				if (j === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: nameckey,
								key: nameckey
							}
						]
					];
				}

				// Set the capacity column on the csv data
				csvData[j][nameckey] = c;
			});
		});

		if (compare.length > 1) {
			combine.forEach(({ c }, k) => {
				// Generate the f column names
				let nameckey = `Combined Capacity (kt)`;

				// Add the column names to the csv headers if this is the first data iteration
				if (k === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: nameckey,
								key: nameckey
							}
						]
					];
				}

				// Set the capacity column on the csv data
				csvData[k][nameckey] = c;
			});
		}

		return {
			csvFileName,
			csvHeaders,
			csvData
		};
	}

	buildRegionSelectError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select at least one region.
			</Alert>
		);
	}

	buildShortDateRangeError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select a date range of at least 10 days.
			</Alert>
		);
	}

	buildLoadingMessage() {
		return (
			<Fragment>
				<div className="h6 mb-3">Chart data loading...</div>
				<Progress animated value="100" />
			</Fragment>
		);
	}

	getSelectedDetail(selected = []) {
		const { pseudoGroups = [] } = this.props;

		if (selected.length === 1 && selected[0] === 'global') {
			return [{ key: 'global', ...config.globalRegion }];
		} else if (selected.length === 1 && selected[0] === 'combine') {
			return [{ key: 'combine', ...config.combinedRegion }];
		} else if (selected.length >= 1) {
			return selected.map((s) => pseudoGroups.find((group) => group.key === s));
		}
	}

	getChartData(dataArr) {
		const details = this.getSelectedDetail(dataArr.map(({ key }) => key));
		// Build the datasets by iterating the allRegions array and reducing it with the smelterData compare field,
		// so that the original order of the regions is maintained
		return dataArr.map(({ key, data }) => {
			const detail = details.find((d) => d.key === key);
			//filled point for combine
			const fCombinePointBackgroundColour =
				key === 'combine' ? { pointBackgroundColor: detail.colour } : {};

			//fStyle will be added to existing active capacity style in configuration
			const fStyle = {
				label: detail.name,
				borderColor: detail.colour,
				...fCombinePointBackgroundColour
			};

			const addStyle = fStyle;

			return {
				data: data.map(({ t, c }) => {
					return {
						t: moment.utc(t).format(config.dspDateFormat),
						y: c
					};
				}),
				...genChartStyle(this.getChartType()),
				...addStyle
			};
		});
	}

	// Individual data lines shown for multiple regions
	buildLineChart() {
		const { smelterCapacity } = this.props;
		const { compareCombine, global, selectedRegions } = this.state;

		const isCombined =
			(compareCombine === 'combine' && !global && selectedRegions.length > 1) ||
			selectedRegions.length <= 1;

		if (Object.keys(smelterCapacity).length === 0) {
			return this.buildLoadingMessage();
		}

		const apiData = isCombined
			? [{ key: 'combine', data: smelterCapacity.combine }]
			: smelterCapacity.compare;

		const datasets = this.getChartData(apiData);

		return (
			<Card className="mb-3">
				{this.buildCardHeader()}
				<CardBody>
					<Container
						fluid
						className="pl-0 container-width"
						style={{ paddingRight: '21px' }}
					>
						<Row noGutters className="mt-0">
							<Col xs="12" sm="12">
								<Line data={{ datasets }} options={cChartOptions} />
							</Col>
						</Row>
					</Container>
				</CardBody>
			</Card>
		);
	}

	render() {
		let { global, shortDateRange, selectedRegions } = this.state;

		return (
			<DocumentTitle title="SAVANT | Copper Smelter Capacity">
				<div className="content-wrapper">
					<Container fluid>
						{/* Help, crumbs and the filter form */}
						<div className="pull-right">
							{smeltersCapacityInlineHelpContent}
						</div>
						<Crumbs path={[{ title: 'Smelter Capacity' }]} />
						{this.buildFilterForm()}

						{/* Error messages */}
						{shortDateRange && this.buildShortDateRangeError()}
						{!shortDateRange &&
							!global &&
							!selectedRegions.length &&
							this.buildRegionSelectError()}

						{/* Different chart types */}
						{!shortDateRange &&
							((!global && selectedRegions.length > 0) ||
								(global && !selectedRegions.length)) &&
							this.buildLineChart()}
					</Container>
				</div>
			</DocumentTitle>
		);
	}
}

const mapStateToProps = ({ newestDate, pseudoGroups, smelterCapacity }) => {
	let countryGroups = [];
	let chinaGroups = [];
	let categoryGroups = [];
	let hasAdvancedGroups = false;
	if (pseudoGroups && pseudoGroups.length > 0) {
		countryGroups = pseudoGroups
			.filter((grp) => {
				return grp.type === 'country';
			})
			.map(({ key, name }) => {
				return {
					value: key,
					label: name
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		chinaGroups = pseudoGroups
			.filter((grp) => {
				return grp.type === 'china';
			})
			.map(({ key, name }) => {
				return {
					value: key,
					label: name
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		categoryGroups = pseudoGroups
			.filter((grp) => {
				return grp.type === 'category';
			})
			.map(({ key, name }) => {
				return {
					value: key,
					label: name
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		hasAdvancedGroups =
			countryGroups.length > 0 ||
			chinaGroups.length ||
			categoryGroups.length > 0;
	}

	return {
		newestDate,
		pseudoGroups,
		smelterCapacity,
		countryGroups,
		chinaGroups,
		categoryGroups,
		hasAdvancedGroups
	};
};

const mapDispatchToProps = (dispatch) => ({
	fetchNewestDate: () => dispatch(fetchNewestDate()),
	resetNewestDate: () => dispatch(resetNewestDate()),
	fetchSmelterCapacity: (filterData) =>
		dispatch(fetchSmelterCapacity(filterData)),
	resetSmelterCapacity: () => dispatch(resetSmelterCapacity()),
	fetchPseudoGroups: (filterData) => dispatch(fetchPseudoGroups(filterData))
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withRouter(SmeltersCapacity));
